import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext } from 'react'
import useEntity from '../hooks/useEntity'
import { DataPromise, getDataEmptyState } from './AxiosContext'
import { SortDirection, SortSetting } from '../config/settingConfig'
import { ComboBoxOption, sortOptions } from '../components/Dropdown/BaseDropdown'
import useAxios from '../hooks/useAxios'
import AuthorizationContext from './AuthorizationContext'

export enum FileStatus {
    Open = 'OPEN',
    Archived = 'ARCHIVED',
}

export type FileListItemDto = {
    id: string
    name: string
    normalizedName: string
    reference: string
    status: FileStatus
    documentRootId: string
    kyc: boolean
}

export type FileContextType = {
    files: DataPromise<FileListItemDto[]>
    resetFiles: () => void
    upsertFile: (file: FileListItemDto) => void
    validateReferences: (references: string[]) => Promise<Map<string, string>>
}

export function sortFileOptions<F extends FileListItemDto>(fileSort?: SortSetting) {
    return sortOptions<ComboBoxOption<F>>(
        fileSort?.direction === SortDirection.Ascending,
        (option: ComboBoxOption<F>) => option.data?.[fileSort?.field] || option.data.reference,
    )
}

export function mapFileOptions<F extends FileListItemDto>(fileSort?: SortSetting, idProp?: string) {
    return (file: F) => ({
        key: file[idProp] || file.reference,
        text: fileSort?.field === 'name' ? `${file.name} (${file.reference})` : `${file.reference} - ${file.name}`,
        data: file,
    })
}

const FileContext = createContext<FileContextType>({
    files: getDataEmptyState(),
    resetFiles: undefined,
    upsertFile: undefined,
    validateReferences: undefined,
})

export function FileProvider(props: PropsWithChildren): ReactElement {
    const axios = useAxios()
    // Re-fetch /file/list whenever the (user, environment) tuple changes. This is a defensive dependency.
    // See AuthorizationContext for the contract on `environmentKey`.
    const { environmentKey } = useContext(AuthorizationContext)
    const [files, , resetFiles] = useEntity<FileListItemDto[]>(
        () => ({ method: 'GET', url: '/file/list' }),
        false,
        result => result.data,
        [],
        undefined,
        [environmentKey],
    )

    return (
        <FileContext.Provider
            value={{
                files,
                resetFiles: () => resetFiles(),
                upsertFile: (file: FileListItemDto) => {
                    files.data = [...files?.data?.filter(item => item.id !== file.id), file]
                },
                validateReferences: (references: string[]) =>
                    references?.length > 0
                        ? axios
                              .get<{ [key: string]: string }>('/file/reference/validate', { params: { references } })
                              .then(response => new Map(Object.entries<string>(response.data)))
                        : Promise.resolve(new Map()),
            }}
        >
            {props.children}
        </FileContext.Provider>
    )
}

export default FileContext
